import { ReactNode } from 'react'

export enum MapTypeId {
  Sector = 'sec',
  Dow = 'sec_dji',
  Russell = 'sec_rut',
  Nasdaq = 'sec_ndx',
  World = 'geo',
  SectorFull = 'sec_all',
  ETF = 'etf',
  Screener = 'screener',
  Portfolio = 'portfolio',
  ETFHoldings = 'etf_holdings',
  ManagersAndFunds = 'managers_and_funds',
  CryptoUSD = 'crypto',
  CryptoUSDT = 'crypto_usdt',
  CryptoEUR = 'crypto_eur',
  CryptoBTC = 'crypto_btc',
  Futures = 'futures',
}

export enum MapSubtypeId {
  IntradayPerf1m = 'i1',
  IntradayPerf2m = 'i2',
  IntradayPerf3m = 'i3',
  IntradayPerf5m = 'i5',
  IntradayPerf10m = 'i10',
  IntradayPerf15m = 'i15',
  IntradayPerf30m = 'i30',
  IntradayPerf1h = 'h',
  IntradayPerf2h = '2h',
  IntradayPerf4h = '4h',
  IntradayPerfAH = 'ah',
  DayPerf = 'd1',
  WeekPerf = 'w1',
  MonthPerf = 'w4',
  ThreeMonthPerf = 'w13',
  SixMonthPerf = 'w26',
  YearPerf = 'w52',
  MonthToDatePerf = 'mtd',
  YearToDatePerf = 'ytd',
  ThreeYearPerf = '3y',
  FiveYearPerf = '5y',
  TenYearPerf = '10y',
  RelativeVolume = 'relvol',
  PriceToEarnings = 'pe',
  ForwardPriceToEearnings = 'fpe',
  PriceEarningsToGrowth = 'peg',
  PriceToSellsRatio = 'ps',
  PriceToBookRatio = 'pb',
  DividendYield = 'div',
  EPSGrowth5y = 'eps5y',
  EPSGrowth3y = 'eps3y',
  EPSThisYear = 'epsthisyear',
  EPSNextYear = 'epsnextyear',
  EPSQuarterOverQuarter = 'epsqoq',
  EPSNext5Years = 'epsnext5y',
  EPSTTM = 'epsttm',
  SalesQuarterOverQuarter = 'salesqoq',
  SalesTTM = 'salesttm',
  InsiderTransactions = 'insidertrans',
  ROA = 'roa',
  ROE = 'roe',
  ROIC = 'roic',
  QuickRatio = 'quickratio',
  CurrentRatio = 'currentratio',
  LTDebtEquity = 'ltdebteq',
  DebtEquity = 'debteq',
  GrossMargin = 'grossmargin',
  OperatingMargin = 'operatingmargin',
  NetMargin = 'netmargin',
  SalesGrowth3y = 'sales3y',
  SalesGrowth5y = 'sales5y',
  EPSSurprise = 'epssurprise',
  SalesSurprise = 'salessurprise',
  FloatShort = 'short',
  AnalystRecommendation = 'rec',
  EarningsPerf = 'earnperf',
  EarningsDate = 'earndate',
  DividendGrowth1y = 'div1y',
  DividendGrowth3y = 'div3y',
  DividendGrowth5y = 'div5y',
  EVEbitda = 'evebitda',
  EVSales = 'evsales',
  // Portfolio
  PortfolioGainPct = 'portfolioGainPct',
  PortfolioGainUsd = 'portfolioGainUsd',
  PortfolioChangePct = 'portfolioChangePct',
  PortfolioChangeUsd = 'portfolioChangeUsd',
  ManagersAndFunds = 'managersAndFunds',
}

export enum ScaleId {
  IntradayReduced = 'intradayReduced',
  DayPerf = 'default',
  WeekPerf = '_5',
  MonthPerf = '_10',
  ThreeMonthPerf = '_15',
  SixMonthPerf = '_20',
  YearPerf = '_25',
  ThreeYearPerf = '_30',
  FiveYearPerf = '_40',
  TenYearPerf = '_50',
  RelativeVolume = '_relvol',
  PriceToEarnings = '_pe',
  ForwardPriceToEearnings = '_fpe',
  PriceEarningsToGrowth = '_peg',
  PriceToSellsRatio = '_ps',
  PriceToBookRatio = '_pb',
  DividendYield = '_div',
  EPSGrowth5y = '_eps5y',
  EPSThisYear = '_epsthisyear',
  EPSNextYear = '_epsnextyear',
  EPSQuarterOverQuarter = '_epsqoq',
  EPSTTM = '_epsttm',
  EPSNext5Years = '_epsnext5y',
  SalesQuarterOverQuarter = '_salesqoq',
  SalesTTM = '_salesttm',
  ROA = '_roa',
  ROE = '_roe',
  ROIC = '_roic',
  QuickRatio = '_quickratio',
  CurrentRatio = '_currentratio',
  LTDebtEquity = '_ltdebteq',
  DebtEquity = '_debteq',
  GrossMargin = '_grossmargin',
  OperatingMargin = '_operatingmargin',
  NetMargin = '_netmargin',
  InsiderTransactions = '_insidertrans',
  FloatShort = '_short',
  AnalystRecommendation = '_rec',
  EarningsDate = '_earndate',
  DividendGrowth = '_divgrowth',
  SalesGrowth = '_salesgrowth',
  EPSSurprise = '_epssurprise',
  SalesSurprise = '_salessurprise',
  EVEbitda = '_evebitda',
  EVSales = '_evsales',
  // Portfolio
  PortfolioPct = '_portfolioPct',
  PortfolioUsd = '_portfolioUsd',
  PercentHundred = '_percentHundred',
}

export const enum NodeDepth {
  Root,
  Sector,
  Industry,
  Node,
}

export interface MapDataRow {
  name: string
  perf?: number
  x: number
  y: number
  dx: number
  dy: number
  data: any
  depth: NodeDepth
}

export interface MapDataRoot {
  name: string
  children: MapDataSector[]
  duplicateTickers: number
  hash: string
}

export interface MapDataSector extends MapDataRow {
  children: MapDataIndustry[]
  parent: MapDataRoot
}

export interface MapDataIndustry extends MapDataRow {
  children: MapDataNode[]
  parent: MapDataSector
}

export interface MapDataNode extends MapDataRow {
  parent: MapDataIndustry
  additional?: string
  description: string
  perfText: string
  value: number
}

export interface MapData {
  sectors: MapDataSector[]
  industries: MapDataIndustry[]
  nodes: MapDataNode[]
}

export type PerfData = {
  nodes: Record<string, number> | Array<MapDataNode>
  additional: Record<string, string>
  subtype: MapSubtypeId
  version?: number
  hash: string
}

export interface MapSubtype {
  value: MapSubtypeId
  label: string
  scaleId: ScaleId
  tooltip: string
  countIndustryPerf: boolean
  countSectorPerf: boolean
}

export interface MapType {
  name: string
  pageTitle: string
  nameShort?: string
  description: string
  type: MapTypeId
  subtypes: Array<{ items: MapSubtype[] }>
  isHidden?: boolean
}

export interface Scale {
  name: string
  tooltip: ReactNode
  id: ScaleId
}
